import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { commonPaths } from '@/modules/common/routes.tsx';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import { VerificationLoadingScreen } from '@/modules/entity/components/VerificationLoadingScreen.tsx';
import { RenderAssociationEntityDetails } from '@/modules/entity/components/entity-info/AssociationEntityInfo.tsx';
import { RenderCompanyEntityDetails } from '@/modules/entity/components/entity-info/CompanyEntityInfo.tsx';
import { RenderIndividualEntityDetails } from '@/modules/entity/components/entity-info/IndividualEntityInfo.tsx';
import { RenderPartnershipEntityDetails } from '@/modules/entity/components/entity-info/PartnershipEntityInfo.tsx';
import { RenderSmsfEntityDetails } from '@/modules/entity/components/entity-info/SmsfEntityInfo.tsx';
import { RenderTrustEntityDetails } from '@/modules/entity/components/entity-info/TrustEntityInfo.tsx';
import {
  useEntityItemUncachedQuery,
  useSetEntityAwaitingApprovalMutation,
} from '@/modules/entity/queries/entity.tsx';
import { ShareholderDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { CustomCheckbox } from 'ui';
import { EntityType } from 'api';

export const Confirmation = ({ allowSkip, moveForward, roads }: OnboardingStepProps) => {
  const [params] = useSearchParams();
  const [isInformationIsTrue, setIsInformationIsTrue] = useState(false);
  const [isPolicy, setIsPolicy] = useState(false);
  const [isBron, setIsBron] = useState(false);
  const [isAcknowledgementsAndDeclarations, setIsAcknowledgementsAndDeclarations] = useState(false);
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));
  const getSubStep = useCallback((step: number) => +(params.get(`sub_step${step}`) ?? 0), [params]);
  const getSubStepRoad = useCallback(
    (step: number, subStep: number) => +(params.get(`sub_step_road${step}_${subStep}`) ?? 0),
    [params],
  );
  const getPercentWithSubRoad = useCallback(
    (shareholders: ShareholderDetails[], step: number, allSubStep = 2) => {
      const filteredShareholders = shareholders?.filter(item => !item.same_as_index);
      if (!filteredShareholders || filteredShareholders?.length === 0) {
        return 100;
      }
      const countShareholders = (filteredShareholders?.length ?? 1) * allSubStep;
      const subStep = getSubStep(step);
      const subStepRoad = getSubStepRoad(step, subStep);
      const percent = ((subStep * allSubStep + (subStepRoad + 1)) / countShareholders) * 100;
      return percent;
    },
    [getSubStep, getSubStepRoad],
  );

  const isFilledRequiredFields = useMemo((): {
    stepsNeedRequired: string[];
    completed: boolean;
  } => {
    const steps = roads?.slice(0, -2); //Del Confirmation and Completion steps
    const stepsNeedRequired: string[] = [];
    let completed = true;

    steps?.forEach((_, i) => {
      const { getNavProgress, navName } = steps[i];
      const currentProgress = getNavProgress({
        data,
        getSubStep,
        getPercentWithSubRoad,
        step: i,
      });

      if (!currentProgress.completed) {
        completed = false;
        stepsNeedRequired.push(navName);
      }

      return currentProgress.completed;
    });

    return { stepsNeedRequired, completed };
  }, [data, getPercentWithSubRoad, getSubStep]);

  const { mutateAsync: setEntityAwaitingApproval, isLoading } =
    useSetEntityAwaitingApprovalMutation({
      onSuccess: moveForward,
    });

  const renderInfo = () => {
    if (data?.entity.type === EntityType.Individual) {
      return (
        <RenderIndividualEntityDetails
          individual_entity_details={data.entity?.individual_entity_details}
          {...data.entity}
        />
      );
    }

    if (data?.entity.type === EntityType.Company) {
      return (
        <RenderCompanyEntityDetails
          company_entity_details={data.entity?.company_entity_details}
          {...data.entity}
        />
      );
    }

    if (data?.entity.type === EntityType.Trust) {
      return (
        <RenderTrustEntityDetails
          trust_entity_details={data.entity?.trust_entity_details}
          {...data.entity}
        />
      );
    }

    if (data?.entity.type === EntityType.Smsf) {
      return (
        <RenderSmsfEntityDetails
          smsf_entity_details={data.entity?.smsf_entity_details}
          {...data.entity}
        />
      );
    }

    if (data?.entity.type === EntityType.Partnership) {
      return (
        <RenderPartnershipEntityDetails
          partnership_entity_details={data.entity?.partnership_entity_details}
          {...data.entity}
        />
      );
    }

    if (data?.entity.type === EntityType.Association) {
      return (
        <RenderAssociationEntityDetails
          association_entity_details_id={data.entity?.association_entity_details_id}
          {...data.entity}
        />
      );
    }

    return <></>;
  };

  if (isLoading) {
    return <VerificationLoadingScreen />;
  }

  return (
    <OnboardingWrapper
      title={'Check everything carefully'}
      onClickContinue={() => {
        if (!isFilledRequiredFields.completed) {
          return;
        }

        setEntityAwaitingApproval(+entity_id);
      }}
      buttonContinueHint={
        !isFilledRequiredFields.completed ? (
          <>
            You cannot proceed with confirmation until these steps are completed:
            <br />
            {isFilledRequiredFields.stepsNeedRequired.map((step, i) => (
              <span key={i}>
                - {step}
                <br />
              </span>
            ))}
          </>
        ) : undefined
      }
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={isLoading}
      disableNext={
        !isInformationIsTrue ||
        !isPolicy ||
        !isBron ||
        !isAcknowledgementsAndDeclarations ||
        isLoading ||
        !isFilledRequiredFields.completed
      }
    >
      <div className="w-full flex flex-col gap-5">
        {renderInfo()}
        <div className="w-full flex flex-col gap-2 pt-5">
          <CustomCheckbox
            onChange={e => setIsInformationIsTrue(e.target.checked)}
            checked={isInformationIsTrue}
            label="I/We confirm the above information is true and correct"
            className="[&>.ant-checkbox]:self-start [&>.ant-checkbox]:pt-1"
          />
          <CustomCheckbox
            onChange={e => setIsPolicy(e.target.checked)}
            checked={isPolicy}
            className="[&>.ant-checkbox]:self-start [&>.ant-checkbox]:pt-1"
            label={
              <div className="flex flex-row">
                I/We consent to the{' '}
                <a target="_blank" href={commonPaths.static.privacyPolicy} className="flex">
                  <span className="text-blue-500 ml-[5px] hover:underline hover:cursor-pointer">
                    Compli One data handling and privacy policy
                  </span>
                </a>
              </div>
            }
          />
          <CustomCheckbox
            onChange={e => setIsAcknowledgementsAndDeclarations(e.target.checked)}
            checked={isAcknowledgementsAndDeclarations}
            className="[&>.ant-checkbox]:self-start [&>.ant-checkbox]:pt-1"
            label={
              <div className="flex flex-row">
                I/We consent to{' '}
                <a
                  target="_blank"
                  href="https://complione.com.au/acknowledgements-and-declarations/" //{commonPaths.static.acknowledgementsAndDeclarations}
                  className="flex"
                >
                  <span className="text-blue-500 ml-[5px] hover:underline hover:cursor-pointer">
                    Acknowledgements and Declarations
                  </span>
                </a>
              </div>
            }
          />
          <CustomCheckbox
            onChange={e => setIsBron(e.target.checked)}
            checked={isBron}
            className="[&>.ant-checkbox]:self-start [&>.ant-checkbox]:pt-1"
            label={
              'I/We confirm that I am authorised to provide the personal details presented in this form, ' +
              'and I consent to this information being checked with the appropriate document issuers, ' +
              'official record holders, credit bureaus, and mobile providers, including superannuation and employment verification,' +
              ' or other third-party systems via bronID (an Identity Service Provider),' +
              ' for the purposes of identity verification and AML/CTF compliance. ' +
              'I consent to the details and results from this identity verification being stored ' +
              'for audit and compliance purposes with the Identity Service Provider (bronID)'
            }
          />
        </div>
      </div>
    </OnboardingWrapper>
  );
};
